import { useEffect, useMemo, useRef } from 'react'
import { browserHistory } from 'react-router'
import { connect } from 'react-redux'
import { selectShowCallHandler } from '../App/selectors'
import { OptionsHOC } from '../../hocs'
import BotIcon from '../../components/BotIcon'
import { addMessageListener, getCustomer, replaceScriptNodes } from '../../helpers/utils'
import { createLexWebUiScriptTag, handleExistingBotWindow, handleBotWindowReference, loadReactBot, postMessageToBotWindow } from '../../helpers/bot'
import { BOT_TYPE } from './constants'
import * as KEYS from '../../constants/keys'

// routes.jsx redirects faqs/:id -> faq/:id and tutorials/:id -> tutorial/:id, so
// a dbNav of /faqs/123 settles at /faq/123. Compare against where it actually
// lands, or the "already showing it" check below never matches and every bot
// turn re-pushes the same navigation.
const REDIRECTED_SEGMENTS = { faqs: 'faq', tutorials: 'tutorial' }

const resolveDbNavPath = value => {
  const [, head, tail] = value.match(/^\/([^/?#]+)(.*)$/) || []
  const singular = REDIRECTED_SEGMENTS[head]
  // require a tail so the /faqs list route is not rewritten to /faq
  return singular && tail.startsWith('/') ? `/${singular}${tail}` : value
}

/**
 * Used to listen for messages from the parent window.
 *
 * The function listens for four types of messages:
 * 1) BOT_INTERACTION - Enables bot-driven navigation after the first user interaction.
 * 2) dbNav - This message type is sent when a user clicks on a link in the bot's response.
 *    When this message type is received, it will navigate to the URL specified in the value
 *    property of data object passed into addMessageListener as an argument.
 * 3) botExternalClose - This message type is sent when a user clicks on "End Call" button within
 *    Bot UI (iframe). When this message type is received, it will click on "Stop Call" button in
 *    the parent window.
 * 4) BOT_READY - Indicates that the bot UI has completed loading.
 *    When received:
 *      • If the bot is opened in a separate window, sends
 *        `AUTH_TOKEN`, `USER_ID`, and `USER_EMAIL` as separate events
 *        to the bot window.
 *      • Otherwise, locates the bot iframe and sends the same
 *        authentication details to its contentWindow.
 * 
 * *
 * @param {React.MutableRefObject<boolean>} navigationEnabledRef
 *        Ref controlling whether bot navigation is allowed.
 *
 * @returns {() => void} Cleanup function to remove all listeners.
 */

const registerEventListener = botNavigationEnabledRef => {
  const cleanups = []
  cleanups.push(
    addMessageListener('BOT_INTERACTION', () => {
      botNavigationEnabledRef.current = true
    }))
  cleanups.push(
    addMessageListener('dbNav', data => {
      if (!botNavigationEnabledRef.current) return
      if (!data?.value) return
      const currentFullPath =
        window.location.pathname +
        window.location.search +
        window.location.hash
      if (resolveDbNavPath(data.value) === currentFullPath) return

      browserHistory.push(data.value)
    })
  )
  cleanups.push(
    addMessageListener('botExternalClose', () => {
      const closeButton = document.getElementById('stopCall')
      if (closeButton) {
        closeButton.click()
      }
    })
  )
  cleanups.push(
    addMessageListener('BOT_READY', () => {
      // Re-send the active call session id (if any) so a bot that loads/reloads
      // mid-call picks it up; sent as '' when no call is active so the widget clears it.
      const callSessionId = sessionStorage.getItem(KEYS.CALL_SESSION_ID) || ''
      if (window.botWindowReference) {
        postMessageToBotWindow({ event: 'AUTH_TOKEN', token: localStorage.getItem('id_token') }, '*')
        postMessageToBotWindow({ event: 'USER_ID', id: localStorage.getItem('user_id') }, '*')
        postMessageToBotWindow({ event: 'USER_EMAIL', email: localStorage.getItem('user_email') }, '*')
        postMessageToBotWindow({ event: 'CALL_SESSION_ID', call_session_id: callSessionId }, '*')
      } else {
        // For chat bot iframe
        const iframe = document.querySelector('#bot-iframe-container iframe') || document.querySelector('#lex-web-ui-iframe iframe');
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage(
            { event: 'AUTH_TOKEN', token: localStorage.getItem('id_token') },
            '*'
          );
          iframe.contentWindow.postMessage(
            { event: 'USER_ID', id: localStorage.getItem('user_id') },
            '*'
          );
          iframe.contentWindow.postMessage(
            { event: 'USER_EMAIL', email: localStorage.getItem('user_email') },
            '*'
          );
          iframe.contentWindow.postMessage(
            { event: 'CALL_SESSION_ID', call_session_id: callSessionId },
            '*'
          );
        }
      }
    })
  )
  return () => {
    cleanups.forEach(cleanup => cleanup())
  }
}

/**
 * The BotContainer component is a React component that renders a bot widget.
 * It uses the useEffect hook to perform various tasks based on the bot options.
 *
 * 1. Registers event listeners for 'dbNav' and 'botExternalClose' messages.
 * 2. Checks if the bot is a QnABot and in windowed mode, and if so, handles the existing bot tab.
 * 3. If it's a QnABot or the 'override' option is set, it mounts the appropriate bot script to the DOM.
 * 4. If the bot name is not provided, it exits early. (Analytics legacy)
 * 5. Otherwise, it loads the bot script and initializes the DbChatWidget with the provided bot options.
 *
 * @param {function} getOptionObject - A function that takes a path to an option object and returns the object. If the path is not found, it will return an empty object.
 * @returns {JSX.Element} A component that writes and/or initializes elements directly on the DOM.
 */
const BotContainer = ({ getOptionObject, showCallHandler }) => {

  // getOptionObject rebuilds its result on every call, so spreading it
  // straight into botOptions produced a new identity each render — and since
  // botOptions is in the loader effect's dep array below, the effect re-ran on
  // every render, re-injecting bot scripts and re-registering message
  // listeners. Key the memo on the resolved CONTENT so the identity only moves
  // when the options actually change. Safe to round-trip: these are plain JSON
  // config values from the param store.
  const botOptionsKey = JSON.stringify(getOptionObject('bot'))
  const botOptions = useMemo(() => JSON.parse(botOptionsKey), [botOptionsKey])
  const customer = getCustomer()

  const {
    external,
    override,
    name,
    windowed,
    isQNA,
    launcherIcon,
    baseUrl,
    title,
    bubbleText,
    bubbleDelay,
    type
  } = botOptions
  // Whether the bot must wait for a call session. Gate on the call handler being
  // reachable, not merely enabled: `showCallHandler` also folds in the overlay
  // query param, and the call handler UI only renders when it is true — keying
  // off `callHandler.enabled` alone would leave an overlay-suppressed page
  // waiting forever for a call session nobody can start.
  //
  // Kept in a ref and deliberately NOT a dependency of the loader effect below.
  // Ordinary browserHistory pushes drop the overlay param, so this value flips
  // mid-session, and that effect's ReactBot branch returns without cleanup — a
  // re-run would append a second loader script, IframeLoader, iframe and
  // launcher beside the live bot. The widget only reads the setting at config
  // time anyway, and re-mounting to change it would discard the conversation.
  const requireCallSessionRef = useRef(false)
  useEffect(() => {
    requireCallSessionRef.current = !!showCallHandler
  }, [showCallHandler])

  const botNavigationEnabledRef = useRef(false)

  // Mount-scoped and independent of any bot option, so this registers exactly
  // once for the life of the container. BOT_INTERACTION is a one-shot signal
  // from the widget (fired on the agent's first message), so the gate must not
  // be tied to the loader effect below re-running — a re-render must not
  // disarm dbNav after the widget has already told us it's safe to navigate.
  useEffect(() => registerEventListener(botNavigationEnabledRef), [])

  useEffect(() => {
    /**
     * Check if the bot is a QnABot or type is equal BOT_TYPE.VERSION2 or BOT_TYPE.VERSION3_1 and windowed .
     *
     * If both conditions are true, it handles the existing bot tab and exits early.
     */
    if (
      (isQNA || type === BOT_TYPE.VERSION2 || type === BOT_TYPE.VERSION3_1) &&
      windowed
    ) {
      handleExistingBotWindow()
      return;
    }

    /**
     * Otherwise mount a bot script to the DOM.
     *
     * Checks if the bot is a QnABot or type is BOT_TYPE.VERSION2 and not windowed.
     * Either a loader script for QnA's LexWebUI iframe, or the value of 'override', is then inserted into the DOM element with the ID 'scripts'.
     */
    if (isQNA || override || type === BOT_TYPE.VERSION2) {
      const scriptValue = ((isQNA || type === BOT_TYPE.VERSION2) && !windowed)
        ? createLexWebUiScriptTag(baseUrl, customer)
        : override;
      const scriptsDomElement = document.getElementById('scripts')
      scriptsDomElement.insertAdjacentHTML('beforeend', scriptValue)

      /**
       * this is (apparently) needed to execute the script, if not it will be ignored
       * @todo reconsider refactor as it duplicates global aws sdk at parent
       */
      replaceScriptNodes(scriptsDomElement)
      return
    }

    /**
     * Checks if the type is equal to BOT_TYPE.VERSION3_1 and not windowed. In that case, mount the React bot to the DOM
     */

    if (type === BOT_TYPE.VERSION3_1 && !windowed) {
      loadReactBot(baseUrl, customer, requireCallSessionRef.current)
      return
    }


    /**
     * Exit early if the bot name is not provided.
     *
     * Ensures that DbChatWidget initialization process does not proceed as botOptions.name 
     * is required by @devicebits/analytics-client for BQ tracking.
     */
    if (!name) {
      return
    }

    /**
     * Else, load the bot script and DbChatWidget with the provided bot options.
     *
     * This function dynamically loads the bot script from the specified bundle URL.
     * Once the script is loaded, it initializes the DbChatWidget with the given options.
     * The options include backend configuration, bot ID, bot name, and launcher icon.
     */
    window.loadScript(botOptions.bundleUrl, () => {
      window.DbChatWidget.init({
        ...botOptions,
        backend: botOptions.backend || 'botapi',
        botId: botOptions.name,
        botName: botOptions.trackingName || botOptions.name,
        launcher: { icon: botOptions.launcherIcon }
      })
    });
    return () => {
      const loaderScript = document.getElementById('bot-loader-script');
      if (loaderScript) loaderScript.remove();
    };
  }, [windowed, baseUrl, name, isQNA, override, botOptions, type])


  return (isQNA || type === BOT_TYPE.VERSION2 || type === BOT_TYPE.VERSION3_1) &&
    windowed ? (
    <BotIcon
      launcherIcon={launcherIcon}
      handleButtonClick={() => handleBotWindowReference(baseUrl, customer, external)}
      title={title}
      bubbleText={bubbleText}
      bubbleDelay={bubbleDelay}
    />
  ) : (
    <div id="db-chat-widget-root" />
  )
}

const mapStateToProps = state => ({
  showCallHandler: selectShowCallHandler(state)
})

export default connect(mapStateToProps)(OptionsHOC(BotContainer))